/**
 * MentionAutocomplete Component
 *
 * Dropdown autocomplete for @mentions in chat input.
 * Shows matching identities with their service availability, and (optionally) a
 * Workflows section so the user can switch the chat to a workflow straight from
 * the `@` popup. Each workflow row carries a `w1`, `w2` ... shortcut badge, and
 * typing `@w<n>` jumps straight to the nth workflow.
 */

import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { Avatar, Spinner, Link } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";

import { useMentions, useAddRecentIdentity } from "@/hooks/useMentions";
import { IdentitySearchResult } from "@/stores/MentionStore";

import { ServiceBadge, SUPPORTED_SERVICES } from "./ServiceBadge";

/** Minimal workflow shape needed to render a selectable `@` row. */
export interface MentionWorkflow {
  id: string;
  name: string;
  description?: string;
}

interface MentionAutocompleteProps {
  /** Search query (text after @) */
  query: string;
  /** Called when user selects an identity */
  onSelect: (identity: IdentitySearchResult) => void;
  /** Called when autocomplete should close (Escape, Tab, or selection). */
  onClose?: () => void;
  /** Position of the dropdown */
  position: { top: number; left: number };
  /** Whether the dropdown is visible */
  visible: boolean;

  // ── Workflows (optional; purely additive) ───────────────────────────────
  /** Workflows the user can switch to, surfaced above the people results. */
  workflows?: MentionWorkflow[];
  /** Id of the workflow currently active in the chat (marked with a tick). */
  activeWorkflowId?: string | null;
  /** Brand-resolved heading for the Workflows section. */
  workflowsLabel?: string;
  /** Called with a workflow id when a workflow row is chosen. */
  onSelectWorkflow?: (workflowId: string) => void;
}

/** Matches the `@w<n>` quick-pick shortcut (e.g. "w1", "w12"). */
const WORKFLOW_SHORTCUT = /^w(\d+)$/;

/**
 * MentionAutocomplete displays a dropdown of matching identities (and,
 * optionally, workflows) when the user types @ in the chat input.
 *
 * Features:
 * - Keyboard navigation (up/down arrows, enter to select, escape to close)
 * - Workflows section with `w1`..`wN` shortcut badges; `@w<n>` jumps to one
 * - Shows service availability badges (Slack, Email, Teams, Google)
 * - Displays recent identities when query is empty
 * - Caches results for performance
 */
export const MentionAutocomplete: React.FC<MentionAutocompleteProps> = ({
  query,
  onSelect,
  onClose,
  position,
  visible,
  workflows,
  activeWorkflowId,
  workflowsLabel,
  onSelectWorkflow,
}) => {
  const { t } = useTranslation();
  const { results, loading, error, setQuery } = useMentions();
  const addRecentIdentity = useAddRecentIdentity();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // ── Workflow rows for the current query ────────────────────────────────
  // `wIndex` is the 1-based position in the FULL list, so the `w{n}` badge is
  // stable and `@w<n>` always resolves to the same workflow regardless of the
  // active text filter.
  const trimmedQuery = query.trim().toLowerCase();
  const hasWorkflows = (workflows?.length ?? 0) > 0;

  // Workflows are scoped behind a leading "w" so the bare "@" stays free for
  // people, skills and anything else added later. "@w" lists all workflows,
  // "@w<n>" jumps to the nth. Any other query (including empty) is not workflows.
  const inWorkflowScope = hasWorkflows && /^w\d*$/.test(trimmedQuery);

  const filteredWorkflows = useMemo(() => {
    if (!inWorkflowScope) return [];

    const all = (workflows ?? []).map((wf, i) => ({ wf, wIndex: i + 1 }));

    if (trimmedQuery === "w") return all;

    const shortcut = WORKFLOW_SHORTCUT.exec(trimmedQuery);

    if (shortcut) {
      const n = parseInt(shortcut[1], 10);

      return all.filter(({ wIndex }) => wIndex === n);
    }

    return all;
  }, [inWorkflowScope, workflows, trimmedQuery]);

  const workflowCount = filteredWorkflows.length;

  // While scoped to workflows ("@w…"), the popup shows workflows only; otherwise
  // it shows people (and a hint that `@w` lists workflows).
  const showPeopleSection = !inWorkflowScope;
  const totalRows = workflowCount + (showPeopleSection ? results.length : 0);

  // Update search query when prop changes
  useEffect(() => {
    setQuery(query);
    setSelectedIndex(0);
  }, [query, setQuery]);

  // Handle identity selection
  const handleSelect = useCallback(
    (identity: IdentitySearchResult) => {
      addRecentIdentity(identity);
      onSelect(identity);
    },
    [onSelect, addRecentIdentity],
  );

  // Keyboard navigation across the combined [workflows, people] list
  useEffect(() => {
    if (!visible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedIndex((prev) => (totalRows === 0 ? 0 : (prev + 1) % totalRows));
          break;
        case "ArrowUp":
          e.preventDefault();
          setSelectedIndex((prev) => (totalRows === 0 ? 0 : (prev - 1 + totalRows) % totalRows));
          break;
        case "Enter": {
          e.preventDefault();
          if (selectedIndex < workflowCount) {
            const picked = filteredWorkflows[selectedIndex];

            if (picked) onSelectWorkflow?.(picked.wf.id);
          } else {
            const identity = results[selectedIndex - workflowCount];

            if (identity) handleSelect(identity);
          }
          break;
        }
        case "Escape":
          e.preventDefault();
          onClose?.();
          break;
        case "Tab":
          // Close on tab
          onClose?.();
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [
    visible,
    results,
    filteredWorkflows,
    workflowCount,
    totalRows,
    selectedIndex,
    handleSelect,
    onSelectWorkflow,
    onClose,
  ]);

  // Scroll selected item into view
  useEffect(() => {
    if (!containerRef.current) return;
    const selectedEl = containerRef.current.querySelector(`[data-index="${selectedIndex}"]`);

    if (selectedEl) {
      selectedEl.scrollIntoView({ block: "nearest" });
    }
  }, [selectedIndex]);

  if (!visible) return null;

  return (
    <div
      ref={containerRef}
      aria-label={t("Mention suggestions")}
      className="fixed z-50 bg-content1 border border-divider rounded-xl shadow-lg max-h-64 overflow-y-auto min-w-70 max-w-sm"
      role="listbox"
      style={{
        top: position.top,
        left: position.left,
        transform: "translateY(-100%)",
      }}
      tabIndex={-1}
      onMouseDown={(e) => e.preventDefault()}
    >
      {/* ── Workflows section (optional) ─────────────────────────────────── */}
      {workflowCount > 0 && (
        <>
          <div className="sticky top-0 z-10 flex items-stretch border-b border-divider bg-content1">
            <div className="flex flex-1 min-w-0 items-center px-3 py-2">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-default-400">
                {workflowsLabel || t("chatCommandMenu.sections.workflows")}
              </span>
            </div>
            {/* Close (X) — pointer/touch dismissal (Esc is unavailable on mobile). */}
            <button
              aria-label={t("Close")}
              className="flex shrink-0 items-center justify-center px-3 min-h-11 text-default-400 hover:text-default-600 hover:bg-default-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset"
              data-id="mention-autocomplete-workflows-close"
              type="button"
              onClick={() => onClose?.()}
            >
              <Icon className="w-4 h-4" icon="solar:close-circle-linear" />
            </button>
          </div>

          {filteredWorkflows.map(({ wf, wIndex }, i) => (
            <div
              key={wf.id}
              aria-selected={i === selectedIndex}
              className={`
                px-3 py-2 cursor-pointer flex items-center gap-3
                hover:bg-default-100 transition-colors
                ${i === selectedIndex ? "bg-default-100" : ""}
              `}
              data-index={i}
              role="option"
              tabIndex={0}
              onClick={() => onSelectWorkflow?.(wf.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onSelectWorkflow?.(wf.id);
                }
              }}
              onMouseEnter={() => setSelectedIndex(i)}
            >
              {/* Shortcut badge — type @w{wIndex} to jump straight here */}
              <span
                aria-hidden="true"
                className="shrink-0 px-1.5 py-0.5 rounded bg-default-100 text-[10px] font-mono font-semibold text-default-500 min-w-[26px] text-center"
              >
                w{wIndex}
              </span>

              {/* Workflow glyph (uniform; per-workflow icons are noisy) */}
              <Icon className="w-4 h-4 text-default-500 shrink-0" icon="solar:widget-2-linear" />

              {/* Name + description */}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{wf.name}</div>
                {wf.description && (
                  <div className="text-xs text-default-500 truncate">{wf.description}</div>
                )}
              </div>

              {/* Active tick, otherwise a chevron */}
              {wf.id === activeWorkflowId ? (
                <Icon
                  aria-label={t("chatCommandMenu.workflows.active")}
                  className="w-4 h-4 text-success-500 shrink-0"
                  icon="solar:check-circle-bold"
                />
              ) : (
                <Icon
                  className="w-3 h-3 text-default-300 shrink-0"
                  icon="solar:alt-arrow-right-linear"
                />
              )}
            </div>
          ))}

          {showPeopleSection && <div className="h-px bg-divider" />}
        </>
      )}

      {/* ── People section ───────────────────────────────────────────────── */}
      {showPeopleSection && (
        <>
          {/* Header */}
          <div className="sticky top-0 z-10 flex items-stretch border-b border-divider bg-content1">
            <div className="flex flex-1 min-w-0 items-center px-3 py-2">
              <span className="text-xs text-default-500">
                {query.length === 0 ? t("Recent mentions") : t("Search results")}
              </span>
            </div>
            {/* Close (X) — pointer/touch dismissal (Esc is unavailable on mobile). */}
            <button
              aria-label={t("Close")}
              className="flex shrink-0 items-center justify-center px-3 min-h-11 text-default-400 hover:text-default-600 hover:bg-default-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset"
              data-id="mention-autocomplete-people-close"
              type="button"
              onClick={() => onClose?.()}
            >
              <Icon className="w-4 h-4" icon="solar:close-circle-linear" />
            </button>
          </div>

          {/* Discoverability hint: workflows live behind `@w` */}
          {query.length === 0 && hasWorkflows && (
            <div className="px-3 py-2 text-[11px] text-default-400 border-b border-divider flex items-center gap-1.5">
              <span
                aria-hidden="true"
                className="px-1.5 py-0.5 rounded bg-default-100 text-[10px] font-mono font-semibold text-default-500"
              >
                w
              </span>
              {t("mentionAutocomplete.workflowsHint")}
            </div>
          )}

          {/* Loading state */}
          {loading && (
            <div aria-live="polite" className="flex items-center justify-center py-4" role="status">
              <Spinner aria-label={t("Loading...")} size="sm" />
            </div>
          )}

          {/* Error state */}
          {error && <div className="px-3 py-4 text-sm text-danger">{error}</div>}

          {/* Empty state */}
          {!loading && !error && results.length === 0 && (
            <div className="px-3 py-4 text-sm text-default-500 text-center">
              {query.length > 0 ? t("No matches found") : t("No recent mentions")}
            </div>
          )}

          {/* Results list */}
          {!loading &&
            !error &&
            results.map((identity, index) => {
              const rowIndex = workflowCount + index;

              return (
                <div
                  key={identity.id}
                  aria-selected={rowIndex === selectedIndex}
                  className={`
                    px-3 py-2 cursor-pointer flex items-center gap-3
                    hover:bg-default-100 transition-colors
                    ${rowIndex === selectedIndex ? "bg-default-100" : ""}
                  `}
                  data-index={rowIndex}
                  role="option"
                  tabIndex={0}
                  onClick={() => handleSelect(identity)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleSelect(identity);
                    }
                  }}
                  onMouseEnter={() => setSelectedIndex(rowIndex)}
                >
                  {/* Avatar */}
                  <Avatar
                    className="flex-shrink-0"
                    name={identity.displayName}
                    size="sm"
                    src={identity.avatarUrl}
                  />

                  {/* Name and email */}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{identity.displayName}</div>
                    <div className="text-xs text-default-500 truncate">
                      {identity.canonicalEmail}
                    </div>
                  </div>

                  {/* Service badges */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {SUPPORTED_SERVICES.map((service) => (
                      <ServiceBadge
                        key={service}
                        available={identity.availableServices.includes(service)}
                        service={service}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
        </>
      )}

      {/* Footer hint — pinned so it stays visible while the list scrolls */}
      <div className="sticky bottom-0 z-10 bg-content1 px-3 py-2 border-t border-divider text-xs text-default-400 flex items-center justify-between">
        <div>
          <span className="mr-4">
            <kbd className="px-1 py-0.5 bg-default-100 rounded text-[10px]">↑↓</kbd> {t("navigate")}
          </span>
          <span className="mr-4">
            <kbd className="px-1 py-0.5 bg-default-100 rounded text-[10px]">Enter</kbd>{" "}
            {t("select")}
          </span>
          <span>
            <kbd className="px-1 py-0.5 bg-default-100 rounded text-[10px]">Esc</kbd> {t("close")}
          </span>
        </div>
        <Link
          className="text-xs text-primary hover:underline flex items-center gap-1"
          href="/settings#mentions"
          size="sm"
        >
          <Icon className="w-3 h-3" icon="solar:settings-linear" />
          {t("Add services")}
        </Link>
      </div>
    </div>
  );
};

export default MentionAutocomplete;
